/**
 * Pools on Ref Finance's classic exchange holding the asset
 * (blackbeard/observer#39, item 2).
 *
 * The asset trades in the DCL pool above. This is the watch for a venue
 * opening somewhere else on Ref: every classic pool is listed every ten
 * minutes, and each one naming the asset is shown, empty or not.
 */

import { useEffect, useState } from 'react'

import type { RefV1Pools } from '../api/generated/RefV1Pools'
import { RequestFailed, fetchRefV1 } from '../api/rest'
import { height as fmtHeight, shortAddress, tokens, when } from '../lib/format'

const REFRESH_MS = 120_000

export function RefV1Panel({
  chain,
  assetContract,
  decimals,
  symbol,
}: {
  chain: string
  /** The asset's token contract, which identifies its column in each pool. */
  assetContract: string
  decimals: number
  symbol: string
}) {
  const [data, setData] = useState<RefV1Pools | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const load = () =>
      fetchRefV1(chain, controller.signal)
        .then((d) => {
          setData(d)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain])

  const label = (t: string) => (t.endsWith('.near') ? t : shortAddress(t))

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Ref classic exchange</h2>
        <span className="eyebrow">
          {data
            ? `${data.contract} · ${fmtHeight(data.scanned)} pools checked · ${when(data.checked_at)}`
            : 'pools holding the asset'}
        </span>
      </div>
      {error && <p className="empty">{error}</p>}
      {!error && data === undefined && <p className="empty">Reading…</p>}
      {!error && data === null && (
        <p className="empty">Not listed yet — the first listing runs shortly after a restart.</p>
      )}
      {data && data.pools.length === 0 && (
        <p className="empty">No pool on the classic exchange holds {symbol}.</p>
      )}
      {data && data.pools.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">Classic Ref pools holding {symbol}</caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Pool
                </th>
                <th scope="col" className="left">
                  Kind
                </th>
                <th scope="col" className="left">
                  Paired with
                </th>
                <th scope="col">{symbol} in it</th>
                <th scope="col">Fee</th>
              </tr>
            </thead>
            <tbody>
              {data.pools.map((p) => {
                const at = p.tokens.indexOf(assetContract)
                const others = p.tokens.filter((_, i) => i !== at)
                return (
                  <tr key={p.pool_id}>
                    <td className="left numeral">#{p.pool_id}</td>
                    <td className="left">{p.kind.toLowerCase().replace('_', ' ')}</td>
                    <td className="left">{others.map(label).join(', ')}</td>
                    <td className="numeral">
                      {at >= 0 ? `${tokens(p.amounts[at] ?? '0', decimals)}` : '—'}
                    </td>
                    <td className="numeral">{(p.fee / 100).toFixed(2)}%</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="panel-note">
        Every pool on Ref&apos;s classic exchange is listed every ten minutes, and each naming{' '}
        {symbol} is shown here, empty or not — it is where a new venue for {symbol} could open
        without trading anywhere this page already follows. The exchange&apos;s own {symbol} balance
        is also one of the named holders in the supply reconciliation.
      </p>
    </section>
  )
}
